<template>
  <div>
    <widget-search
      :form="form"
      :groups="groups"
      @search="onSearch"
      :show="showSearch"
    />
    <widget-table-button :buttons="buttons">
      <widget-search-btn v-model="showSearch" />
    </widget-table-button>
    <widget-table-body
      @change="onOrderChange"
      :columns="columns"
      :list-data="listData"
      :row-selection="rowSelection"
      :select-row-key="schema.primary"
    />
    <!--    分页    -->
    <widget-table-pagination
      :count="listData.count"
      v-model:page-num="service.listConfig.pageNum"
      v-model:page-size="service.listConfig.pageSize"
    />
  </div>
</template>

<script lang="ts" setup>
import { createCrudTableService } from '@/components/business/crud/service/CrudTableService';
import WidgetTableBody from '@/components/business/crud/table/WidgetTableBody.vue';
import WidgetTablePagination from '@/components/business/crud/table/WidgetTablePagination.vue';
import WidgetTableButton from '@/components/business/crud/table/WidgetTableButton.vue';
import { message, Modal } from 'ant-design-vue';
import { windows } from '@/components/common/window/Windows';
import { ref, shallowRef, watch } from 'vue';
import WidgetSearch from '@/components/business/crud/form/WidgetSearch.vue';
import { createSearchService } from '@/components/business/crud/service/CrudSearchService';
import WidgetSearchBtn from '@/components/business/crud/form/WidgetSearchBtn.vue';
import SelectModelDialog from '@/components/business/crud/common/SelectModelDialog.vue';

const props = withDefaults(
  defineProps<{
    title: string; // 用于添加，删除提示词
    status?: 'show' | 'edit'; // 编辑状态
    moduleName: string; // 模块名
    controllerName: string; // 控制器名
    labelName?: string; // 标签属性名
    condition: any; // 移除条件,必填用于查看
    attachCondition?: any; // 添加条件，未提供时不显示添加
    hiddenFields?: string[]; // 隐藏字段
    onChange?: (type: 'attach' | 'detach', data: any[]) => Promise<boolean>; // 更新方法,未提供时，不显示新增及移除
  }>(),
  {
    status: 'show',
    labelName: 'name',
    hiddenFields: () => [],
  }
);
const showSearch = ref(false);
const searchService = createSearchService(
  props.moduleName,
  props.controllerName
);

const { form, groups, getCondition } = searchService.use();

const service = createCrudTableService(props.moduleName, props.controllerName);
service.listConfig.pageSize = 10;
const {
  listData,
  columns,
  buttons,
  rowSelection,
  schema,
  onChange: onOrderChange,
} = service.use();

function onSearch() {
  service.listConfig.condition = {
    $and: [props.condition, getCondition()],
  };
}

searchService.init().then(() => {
  onSearch();
  service.widgetContainer.delField(...props.hiddenFields);
  service.init();
  service.widgetContainer.hideButtonsExclude('view', 'refresh');
  // 添加按钮
  service.widgetContainer.addButton('attach', {
    type: 'table',
    icon: 'PlusOutlined',
    text: '添加',
    handler: async () => {
      const id = windows.open({
        content: {
          type: 'component',
          component: shallowRef(SelectModelDialog),
          props: {
            moduleName: props.moduleName,
            controllerName: props.controllerName,
            labelName: props.labelName,
            condition: props.attachCondition,
          },
        },
        title: '添加' + props.title,
        width: 1280,
        height: 800,
      });
      const selected = await windows.getCloseData(id);
      if (!props.onChange) {
        return;
      }
      const res = await props.onChange('attach', selected);
      if (res) {
        message.success('添加成功');
        await service.refreshList();
      }
    },
  });
  // 移除按钮
  service.widgetContainer.addButton('detach', {
    type: 'row',
    text: '移除',
    confirm: '确定要移除此' + props.title,
    hidden: () => props.status === 'show',
    handler: async (row: any) => {
      if (!props.onChange) {
        return;
      }
      const res = await props.onChange('detach', [row]);
      // 通过父组件删除
      if (res) {
        message.success('移除成功');
        await service.refreshList();
      }
    },
  });
  // 批量移除按钮
  service.widgetContainer.addButton('detachSelect', {
    type: 'select',
    icon: 'DeleteOutlined',
    text: '移除',
    handler: async (rows: any[]) => {
      if (rows.length === 0) {
        message.info('未选中任何项');
        return;
      }
      Modal.confirm({
        title: '确定要移除这' + rows.length + '项吗？',
        onOk: async () => {
          if (!props.onChange) {
            return;
          }
          const res = await props.onChange('detach', rows);
          if (res) {
            message.success('移除成功');
            await service.refreshList();
          }
        },
      });
    },
  });
  watch(
    () => props.status,
    (value) => {
      if (value === 'edit' && props.onChange) {
        service.widgetContainer.showButtons('detach', 'detachSelect');
        if (props.attachCondition) {
          service.widgetContainer.showButtons('attach');
        }
      } else {
        service.widgetContainer.hideButtons('attach', 'detach', 'detachSelect');
      }
    },
    { immediate: true }
  );
});
</script>

<style lang="scss" scoped></style>
